// radius: css border radius
// number: 1
// string: "1px"
// string: "1"
// string: "50%"
// string: "1px 1px 1px 1px"
// string: "50% 0 50% 0"

const emptyRadius = {
  isEmpty: true,
  topLeft: 0,
  topRight: 0,
  bottomLeft: 0,
  bottomRight: 0,
};

export function _resolveRadius(w, h, radius) {
  if (!radius) {
    return emptyRadius;
  }

  // Calculate maximum possible radius based on dimensions
  const maxRadius = Math.min(w, h);

  // Handle percentage values
  if (typeof radius === 'string' && radius.endsWith('%') && !radius.includes(' ')) {
    const percent = parseFloat(radius) / 100;
    const maxSize = Math.max(w, h);
    const value = Math.min(maxSize * percent, maxRadius);
    return {
      topLeft: value,
      topRight: value,
      bottomLeft: value,
      bottomRight: value,
    };
  }

  // Handle single number or pixel value
  if (typeof radius === 'number' || (typeof radius === 'string' && !radius.includes(' '))) {
    const value = Math.min(typeof radius === 'string' ? parseFloat(radius) : radius, maxRadius);
    return {
      topLeft: value,
      topRight: value,
      bottomLeft: value,
      bottomRight: value,
    };
  }

  // Handle multiple values (e.g., "1px 2px 3px 4px" or "50% 0 50% 0")
  if (typeof radius === 'string') {
    const values = radius.split(' ').map((val) => {
      if (val.endsWith('%')) {
        const percent = parseFloat(val) / 100;
        return Math.min(maxRadius * percent, maxRadius);
      }
      return Math.min(parseFloat(val), maxRadius);
    });

    // Handle different number of values according to CSS border-radius rules
    if (values.length === 1) {
      // One value applies to all corners
      return {
        topLeft: values[0],
        topRight: values[0],
        bottomRight: values[0],
        bottomLeft: values[0],
      };
    } else if (values.length === 2) {
      // Two values: first is top-left/bottom-right, second is top-right/bottom-left
      return {
        topLeft: values[0],
        topRight: values[1],
        bottomRight: values[0],
        bottomLeft: values[1],
      };
    } else if (values.length === 3) {
      // Three values: first is top-left, second is top-right/bottom-left, third is bottom-right
      return {
        topLeft: values[0],
        topRight: values[1],
        bottomRight: values[2],
        bottomLeft: values[1],
      };
    } else if (values.length >= 4) {
      // Four values: top-left, top-right, bottom-right, bottom-left
      return {
        topLeft: values[0],
        topRight: values[1],
        bottomRight: values[2],
        bottomLeft: values[3],
      };
    }
  }

  // Default case
  return emptyRadius;
}

export function resolveRadius(w, h, radius) {
  if (!radius || radius === '0px') {
    return emptyRadius;
  }

  const result = _resolveRadius(w, h, radius);
  const { topLeft, topRight, bottomLeft, bottomRight } = result;
  // Calculate the maximum radius based on width and height
  const maxRadius = Math.min(w, h);

  // Check if only one corner has a non-zero radius
  const nonZeroCorners = [topLeft > 0, topRight > 0, bottomLeft > 0, bottomRight > 0].filter(
    Boolean,
  ).length;

  // If only one corner has radius, use maxRadius as limit
  // Otherwise use maxRadius/2 as limit
  const radiusLimit = nonZeroCorners === 1 ? maxRadius : maxRadius / 2;

  // Apply the limits to each corner
  return {
    topLeft: Math.max(0, Math.min(topLeft, radiusLimit)),
    topRight: Math.max(0, Math.min(topRight, radiusLimit)),
    bottomRight: Math.max(0, Math.min(bottomRight, radiusLimit)),
    bottomLeft: Math.max(0, Math.min(bottomLeft, radiusLimit)),
  };
}
